import axios from 'axios';
import constants from '../../config/constants';

export const GET_ATTENDANCE_RANGE = 'GET_ATTENDANCE_RANGE';
export const CLEAR_ATTENDANCE_RANGE = 'CLEAR_ATTENDANCE_RANGE';

export const getAttendanceRange = attendanceRange => ({
  type: GET_ATTENDANCE_RANGE,
  attendanceRange,
});

export const clearAttendanceRange = () => ({
  type: CLEAR_ATTENDANCE_RANGE,
});

//= ========== User routing begin ===========//

// Server-computed daily/weekly/monthly attendance report - the summary numbers
// (present/absent/incomplete days, total working hours) come pre-aggregated instead of being
// approximated client-side from raw per-day session records the way the range endpoint above
// requires. Annual isn't a supported reportType on this endpoint yet, so that view still falls
// back to the range-based client aggregation.
export const fetchAttendanceReportService = async data => {
  const { schoolId, reportType, employeeId, date, fromDate, toDate, month, year, token } = data;
  const res = await axios.get(
    `${constants.API_BASE_URL}/api/attendance/report${constants.makeParams({
      schoolId,
      reportType,
      ...(employeeId && { employeeId }),
      ...(date && { date }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(month && { month }),
      ...(year && { year }),
    })}`,
    constants.setHeader(token)
  );
  return res.data;
};

// Whole-school attendance for a date range, one employee or every employee (employeeId omitted).
// Used to render the staff attendance table without an N+1 per-employee fetch.
export const fetchAttendanceRangeService = data => async dispatch => {
  try {
    const res = await axios.get(
      `${constants.API_BASE_URL}/api/employee-daily-attendance/range${constants.makeParams({
        schoolId: data.schoolId,
        ...(data.employeeId && { employeeId: data.employeeId }),
        fromDate: data.fromDate,
        toDate: data.toDate,
      })}`,
      constants.setHeader(data.token)
    );
    dispatch(getAttendanceRange(res.data));
    return res.data;
  } catch (err) {
    console.error(err.response);
    return [];
  }
};

// Creates the day's attendance record, required before a first session can be added for a
// given employee+date - mirrors the mobile app's own check-in flow (EmployeeAttendance
// container), which does the same create-if-missing before adding a session.
export const createDailyAttendanceApi = data => {
  return axios.post(
    `${constants.API_BASE_URL}/api/employee-daily-attendance`,
    {
      employeeId: data.employeeId,
      schoolId: data.schoolId,
      attendanceDate: data.attendanceDate,
    },
    constants.setHeader(data.token)
  );
};

export const addAttendanceSessionApi = data => {
  return axios.post(
    `${constants.API_BASE_URL}/api/employee-daily-attendance/${data.employeeDailyAttendanceId}/session`,
    {
      employeeId: data.employeeId,
      schoolId: data.schoolId,
      inTime: data.inTime,
      outTime: data.outTime || null,
      status: data.outTime ? 'OUT' : 'IN',
    },
    constants.setHeader(data.token)
  );
};

export const updateAttendanceSessionApi = data => {
  return axios.put(
    `${constants.API_BASE_URL}/api/employee-daily-attendance/session`,
    {
      employeeAttendanceSessionId: data.employeeAttendanceSessionId,
      employeeDailyAttendanceId: data.employeeDailyAttendanceId,
      employeeId: data.employeeId,
      schoolId: data.schoolId,
      inTime: data.inTime,
      outTime: data.outTime || null,
      status: data.outTime ? 'OUT' : 'IN',
    },
    constants.setHeader(data.token)
  );
};

export const deleteAttendanceSessionApi = data => {
  return axios.delete(
    `${constants.API_BASE_URL}/api/employee-daily-attendance/session/${data.employeeAttendanceSessionId}`,
    constants.setHeader(data.token)
  );
};

// Every worker's permission allowance (permissionCount/permissionMinutes) for the school in one
// call - powers the Worker Attendance page's "Salary Impact" tab (late-minutes vs. forgiven
// minutes) without an N+1 per-employee fetch. Money/deduction-amount is intentionally out of
// scope here - there's no reachable per-employee pay rate from the existing salary-structure
// API (only by salaryStrucId or by a designationId Employee records aren't linked to), so this
// stays a minutes-only report rather than guessing a rate.
export const fetchPermissionConfigsApi = data => {
  return axios.get(
    `${constants.API_BASE_URL}/api/employee-permission-config/school/${data.schoolId}`,
    constants.setHeader(data.token)
  );
};

// POST for a first-time config, PUT once employeePermissionConfigId exists - the config page
// decides which by whether it found an existing row for that employee (see PermissionConfigModal).
export const saveEmployeePermissionConfigApi = data => {
  const method = data.employeePermissionConfigId ? 'put' : 'post';
  return axios[method](
    `${constants.API_BASE_URL}/api/employee-permission-config`,
    {
      employeePermissionConfigId: data.employeePermissionConfigId || undefined,
      employeeId: data.employeeId,
      schoolId: data.schoolId,
      permissionCount: data.permissionCount,
      permissionMinutes: data.permissionMinutes,
      status: 'ACTIVE',
    },
    constants.setHeader(data.token)
  );
};

export const deleteEmployeePermissionConfigApi = data => {
  return axios.delete(
    `${constants.API_BASE_URL}/api/employee-permission-config/${data.employeePermissionConfigId}`,
    constants.setHeader(data.token)
  );
};

//= ========== User routing end ===========//
